/**
 * CSVインポート画面の「BlueBean(OPレポート・対応履歴)」(2026-10-11 発注者指示)。
 * 日次【集計】の「CSVアップロード」と同じ取込(CsvUploadModal)を、日付を選ばずにここからも開けるようにする。
 * OPレポートは1日1ファイル(日付はファイル名)、対応履歴は複数日をまとめた1ファイルでもよい(行の日時で日付ごとに分ける)。
 * 下に、月の日ごとに「OPレポート・対応履歴が入っているか」の表を出す(2026-10-12 発注者指示。判定は importStatus.ts)。
 * 表の各行から、その日の OPレポート・対応履歴を削除できる(2026-10-17 発注者指示。違う日付に取り込んだときの取り消し)。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '@core/components/Toast';
import StatusBadge, { type StatusKind } from '@core/components/ui/StatusBadge';
import { todayStr, weekdayLabel } from '@core/lib/date';
import type { Company, DailySummary, DateStr } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchMonthSummaries } from '@core/dashboard/dashboardRepo';
import { formatCount, formatDecimal, NO_DATA } from '@core/utils/formatters';
import CsvUploadModal from './CsvUploadModal';
import ImportDeleteModal from './ImportDeleteModal';
import { importStatusRows, type ImportStatusKind, type ImportStatusRow } from './importStatus';

const STATUS: Record<ImportStatusKind, { kind: StatusKind; label: string; title: string }> = {
  both: { kind: 'confirmed', label: '両方あり', title: 'OPレポートと対応履歴の両方が入っています' },
  op_only: { kind: 'warning', label: 'OPのみ', title: '対応履歴が入っていないため、取得が0件です' },
  activity_only: { kind: 'warning', label: '対応履歴のみ', title: 'OPレポートが入っていないため、稼働時間が0です' },
  none: { kind: 'noData', label: '未取込', title: 'この日はどちらも入っていません' },
  other: { kind: 'info', label: '取込記録なし', title: '日次集計はありますが、CSVの取込記録がありません(サンプル・旧データなど)' },
};

const shiftMonth = (month: string, delta: number) => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export default function BlueBeanUploadCard() {
  const toast = useToast();
  const today = todayStr();
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [opening, setOpening] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [imported, setImported] = useState<DateStr[]>([]);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [summaries, setSummaries] = useState<DailySummary[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [deleting, setDeleting] = useState<ImportStatusRow | null>(null);

  const loadMonth = useCallback(async (m: string) => {
    setSummaries(null);
    setLoadError('');
    try {
      setSummaries(await fetchMonthSummaries(m));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : '読み込めませんでした');
    }
  }, []);

  useEffect(() => {
    void loadMonth(month);
  }, [month, loadMonth]);

  useEffect(() => {
    // 対応履歴の業務コードを会社名で出すため(取込でも使う)
    fetchCompanies().then(setCompanies, () => setCompanies([]));
  }, []);

  async function open() {
    setOpening(true);
    try {
      if (!companies || companies.length === 0) setCompanies(await fetchCompanies());
      setModalOpen(true);
    } catch (e) {
      toast(e instanceof Error ? `会社マスタを読めませんでした: ${e.message}` : '会社マスタを読めませんでした');
    } finally {
      setOpening(false);
    }
  }

  const rows = useMemo(() => (summaries ? importStatusRows(month, summaries, today) : []), [summaries, month, today]);
  const counts = useMemo(() => {
    const c: Record<ImportStatusKind, number> = { both: 0, op_only: 0, activity_only: 0, none: 0, other: 0 };
    for (const r of rows) c[r.status]++;
    return c;
  }, [rows]);
  const companyOfCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of companies ?? []) for (const code of c.bb_business_codes ?? []) m.set(code, c.name);
    return m;
  }, [companies]);

  const sorted = [...imported].sort();
  const [y, m] = month.split('-');
  return (
    <div className="card">
      <h3>BlueBean CSV取込(OPレポート・対応履歴)</h3>
      <p className="hint" style={{ marginBottom: 12 }}>
        OPレポート(agent_report_…csv)と対応履歴(report_detail_…csv)を、まとめて選んで取り込めます。OPレポートは1日1ファイル(日付はファイル名から)、対応履歴は複数日をまとめて出力した1ファイルでも日付ごとに分けて取り込みます。日次【集計】の「CSVアップロード」と同じ取込です。
      </p>
      <div className="modal-actions" style={{ marginTop: 0 }}>
        <button className="btn-green" onClick={() => void open()} disabled={opening}>
          {opening ? '準備中…' : 'ファイルを選んで取り込む'}
        </button>
      </div>
      {sorted.length > 0 && (
        <p className="hint" style={{ marginTop: 12 }}>
          取り込んだ日: {sorted.length}日分({sorted[0]!.replaceAll('-', '/')}〜{sorted[sorted.length - 1]!.replaceAll('-', '/')})。
          <Link to={`/summary/staff?mode=day&date=${sorted[sorted.length - 1]}`}>日次【集計】で見る</Link>
        </p>
      )}

      <h3 style={{ marginTop: 24 }}>日ごとの取込状況</h3>
      <div className="import-status-bar">
        <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="前の月">
          ‹
        </button>
        <b>
          {y}年{Number(m)}月
        </b>
        <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setMonth(shiftMonth(month, 1))} disabled={month >= today.slice(0, 7)} aria-label="次の月">
          ›
        </button>
        {summaries && (
          <span className="hint">
            両方あり {counts.both}日 ・ OPのみ {counts.op_only}日 ・ 対応履歴のみ {counts.activity_only}日 ・ 未取込 {counts.none}日{counts.other ? ` ・ 取込記録なし ${counts.other}日` : ''}
          </span>
        )}
      </div>
      {loadError && <p className="error">読み込めませんでした: {loadError}</p>}
      {!summaries && !loadError && <p className="hint">読み込み中…</p>}
      {summaries && (
        <div className="report-scroll is-auto import-status-scroll">
          <table className="report-table no-stack">
            <caption className="sr-only">日ごとの取込状況</caption>
            <thead>
              <tr>
                <th scope="col">日付</th>
                <th scope="col">状態</th>
                <th scope="col">OPレポート</th>
                <th scope="col">対応履歴</th>
                <th scope="col" className="is-num">取得</th>
                <th scope="col" className="is-num">実稼働</th>
                <th scope="col">操作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = STATUS[r.status];
                return (
                  <tr key={r.date}>
                    <td className="is-nowrap">
                      <Link to={`/summary/staff?mode=day&date=${r.date}`}>
                        {Number(r.date.slice(5, 7))}/{Number(r.date.slice(8))}{weekdayLabel(r.date)}
                      </Link>
                    </td>
                    <td className="is-nowrap">
                      <StatusBadge kind={st.kind} label={st.label} title={st.title} />
                    </td>
                    <td className="is-nowrap">{r.op ? `あり(${formatCount(r.op.rowCount)}行)` : <span className="hint">{NO_DATA}</span>}</td>
                    <td>
                      {r.activity.length === 0 ? (
                        <span className="hint">{NO_DATA}</span>
                      ) : (
                        r.activity.map((a, i) => (
                          <span key={i} className="import-status-act">
                            {a.codes.map((c) => companyOfCode.get(c) ?? c).join('・') || '業務なし'} {formatCount(a.appointments)}件
                          </span>
                        ))
                      )}
                    </td>
                    <td className="is-num">{r.staffRows ? formatCount(r.appointments) : <span className="hint">{NO_DATA}</span>}</td>
                    <td className="is-num">{r.staffRows ? formatDecimal(r.workedHours, { digits: 1, unit: 'h' }) : <span className="hint">{NO_DATA}</span>}</td>
                    <td>
                      {r.status !== 'none' && (
                        <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm ui-btn-danger" onClick={() => setDeleting(r)} disabled={!companies}>
                          削除
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {deleting && companies && (
        <ImportDeleteModal
          row={deleting}
          companies={companies}
          companyOfCode={companyOfCode}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void loadMonth(month);
          }}
        />
      )}
      {modalOpen && companies && (
        <CsvUploadModal
          defaultDate={todayStr()}
          companies={companies}
          onClose={() => setModalOpen(false)}
          onImported={(ds) => {
            setImported((cur) => Array.from(new Set([...cur, ...ds])));
            void loadMonth(month);
          }}
        />
      )}
    </div>
  );
}
